import React, { Component, ReactNode, Suspense, lazy, useState } from 'react';
import { Box, Image } from '@chakra-ui/react';
import YaxleLogo from '../../assets/yaxleLogoBlue.png';

/**
 * Carl's 3D brand mark — the single logo used everywhere Carl shows up
 * (Ask Carl page, order drawer, thinking indicators, tool timeline, search
 * palette). On hover the sparkles burst in and sweep one full loop around
 * the mark — into the page, over the top edge — back to their top-right
 * home; `thinking` keeps them orbiting for waiting animations. The mark
 * itself never spins. For flat nav/tab contexts use CarlSparkleIcon.
 *
 * three.js is code-split behind React.lazy; while it loads (or if WebGL is
 * unavailable) the flat SVG mark renders instead so layout never shifts.
 */

const CarlLogoCanvas = lazy(() => import('./CarlLogoCanvas'));

interface CarlLogoProps {
    /** Square canvas edge, e.g. "32px". The 3D mark fills ~80% of it. */
    size: string;
    /** Continuous sparkle orbit — waiting/"Carl is thinking" mode. */
    thinking?: boolean;
    /** Tilt with the pointer (use on hero sizes only). */
    parallax?: boolean;
    /** Play the hover sparkle-lap once on mount as a welcome animation. */
    welcome?: boolean;
    /** Static rest yaw (radians) — angle the mark to the right on the hero. */
    tilt?: number;
    /** White mark — the brand's alternate for saturated/dark gradients. */
    white?: boolean;
}

const FlatFallback = ({ white }: { white?: boolean }) => (
    <Image
        src={YaxleLogo}
        alt="Carl"
        w="72%"
        h="72%"
        m="14%"
        objectFit="contain"
        draggable={false}
        filter={white ? 'brightness(0) invert(1)' : undefined}
    />
);

class CanvasErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    render() {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}

const CarlLogo = ({ size, thinking = false, parallax = false, welcome = false, tilt = 0, white = false }: CarlLogoProps) => {
    const [hovered, setHovered] = useState(false);
    const fallback = <FlatFallback white={white} />;

    return (
        <Box
            w={size}
            h={size}
            flexShrink={0}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <CanvasErrorBoundary fallback={fallback}>
                <Suspense fallback={fallback}>
                    <CarlLogoCanvas hovered={hovered} thinking={thinking} parallax={parallax} welcome={welcome} tilt={tilt} white={white} />
                </Suspense>
            </CanvasErrorBoundary>
        </Box>
    );
};

export default CarlLogo;
